/**
 * Collapsible Power section. Collapsed (default) shows a compact live summary
 * (total vs CP-max power + a mini bar + active-connector count); expanded
 * reveals the full Power dashboard + Power chart. Keeps the Charge Point tab
 * short for the common case while the detail is one click away.
 */
import { useState } from 'react';
import { useStationStore } from '@/state/useStationStore';
import { PowerDashboard } from './PowerDashboard';
import { PowerChart } from './PowerChart';

export function PowerSection(): JSX.Element {
  const [open, setOpen] = useState(false);
  const connectors = useStationStore((s) => s.connectors);
  const cpMaxPowerW = useStationStore((s) => s.cpMaxPowerW);

  let totalW = 0;
  let active = 0;
  for (const c of connectors.values()) {
    totalW += c.effectivePowerW;
    if (c.effectivePowerW > 0) active += 1;
  }
  const pct = cpMaxPowerW > 0 ? Math.min(1, totalW / cpMaxPowerW) : 0;

  return (
    <section className="rounded border border-slate-700 bg-slate-900/60 p-4" aria-label="Power">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="power-body"
        className="flex w-full items-center justify-between gap-3 text-left focus:outline-none focus:ring-2 focus:ring-primary-base"
        data-testid="power-toggle"
      >
        <span className="flex items-center gap-1.5">
          <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            className={`h-3 w-3 fill-current text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
          >
            <path d="M5.5 3.5L10.5 8l-5 4.5z" />
          </svg>
          <span className="text-sm font-medium text-slate-100">Power</span>
        </span>
        <span className="flex items-center gap-2 text-xs text-slate-400">
          {/* mini live bar (hidden on very narrow widths) */}
          <svg viewBox="0 0 96 6" className="hidden h-1.5 w-24 sm:block" aria-hidden="true">
            <rect x={0} y={0} width={96} height={6} rx={2} className="fill-slate-800" />
            <rect x={0} y={0} width={Math.max(0, pct * 96)} height={6} rx={2} className="fill-primary-base" />
          </svg>
          <span className="font-mono text-slate-200" data-testid="power-mini-total">
            {(totalW / 1000).toFixed(2)} / {(cpMaxPowerW / 1000).toFixed(0)} kW
          </span>
          <span className="hidden text-slate-500 sm:inline">
            · {active} active
          </span>
          <span className="text-slate-500">{open ? 'hide' : 'expand'}</span>
        </span>
      </button>
      {open ? (
        <div id="power-body" className="mt-4 space-y-4">
          <PowerDashboard />
          <PowerChart />
        </div>
      ) : null}
    </section>
  );
}
